import { useContext } from 'react';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useAPI } from '../lib/hooks';
import { AppContext } from '../sporttia/AppContext';
import {
    GetTimetableBookings200,
    GetTimetableParams,
    GetTimeTablePrivileges200,
    GetTimetableRates200,
} from '../types/api/paths/Timetable';

const resource = '/timetables';

export const getTimetableQueryKey = (
    endpoint: string,
    params?: GetTimetableParams,
) => [endpoint, ...(params ? [params] : [])];

export default function useTimetableService() {
    const { migrationApis } = useContext(AppContext)!;
    const [request] = useAPI(migrationApis);

    /**
     * GET /timetable/:id/bookings request.
     */
    function useGetTimetableOccupations<TData = GetTimetableBookings200>(
        params: {
            dateIni: string;
            dateEnd: string;
            fieldId: number;
        },
        queryOptions?: Omit<
            UseQueryOptions<GetTimetableBookings200, unknown, TData>,
            'queryKey' | 'queryFn'
        >,
    ) {
        const endpoint = `${resource}/occupations`;

        const queryKey = getTimetableQueryKey(endpoint, params);

        const queryFn = () =>
            request<GetTimetableBookings200>('GET', endpoint, params);

        const query = useQuery({ queryKey, queryFn, ...queryOptions });

        return {
            ...query,
            queryKey,
        };
    }

    /**
     * GET /timetable/:id/rates request.
     */
    function useGetTimetableRates<TData = GetTimetableRates200>(
        params: {
            dateIni: string;
            dateEnd: string;
            fieldId: number;
        },
        queryOptions?: Omit<
            UseQueryOptions<GetTimetableRates200, unknown, TData>,
            'queryKey' | 'queryFn'
        >,
    ) {
        const endpoint = `${resource}/rates`;

        const queryKey = getTimetableQueryKey(endpoint, params);

        const queryFn = () =>
            request<GetTimetableRates200>('GET', endpoint, params);

        const query = useQuery({ queryKey, queryFn, ...queryOptions });

        return {
            ...query,
            queryKey,
        };
    }

    function useGetTimetablePrivileges<TData = GetTimeTablePrivileges200>(
        params: {
            dateIni: string;
            dateEnd: string;
            fieldId: number;
        },
        queryOptions?: Omit<
            UseQueryOptions<GetTimeTablePrivileges200, unknown, TData>,
            'queryKey' | 'queryFn'
        >,
    ) {
        const endpoint = `${resource}/privileges`;

        const queryKey = getTimetableQueryKey(endpoint, params);

        const queryFn = () =>
            request<GetTimeTablePrivileges200>('GET', endpoint, params);

        const query = useQuery({ queryKey, queryFn, ...queryOptions });

        return {
            ...query,
            queryKey,
        };
    }

    return {
        useGetTimetableOccupations,
        useGetTimetableRates,
        useGetTimetablePrivileges,
    };
}
